import "./App.css";
import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { blobToURL } from "./utils/Utils";
import PagingControl from "./components/PagingControl";
import { AddSigDialog } from "./components/AddSigDialog";
import { Header } from "./Header";
import { BigButton } from "./components/BigButton";
import DraggableSignature from "./components/DraggableSignature";
import DraggableText from "./components/DraggableText";
import axios from 'axios'
import { Steps, message, Popconfirm, Tour, Modal, Spin } from 'antd';
import FontSignatureDialog from "./components/FontSignatureDialog";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

function App() {
	const STATIC_ALLOWED_PARENT_ORIGINS = [
		"https://app.smvrtlegal.com",
		"http://localhost:3300",
		"https://develop.smvrtlegal.com",
	];
	const STATIC_ALLOWED_API_BASES = [
		"https://api1.smvrtlegal.com",
		"https://api2.smvrtlegal.com",
		"http://localhost:7000",
		"https://api4.songsafety.org/",
	];

	// --- Security helpers ---
	const parseEnvList = (value) => {
		const raw = (value || '').trim();
		if (!raw) return [];
		if (raw.startsWith('[')) {
			try {
				const arr = JSON.parse(raw);
				if (Array.isArray(arr)) return arr.map(String);
			} catch (_) { }
		}
		return raw.split(',');
	};

	const uniq = (arr) => Array.from(new Set(arr));

	const getAllowedOrigins = () => {
		const fromEnv = parseEnvList(process.env.REACT_APP_PARENT_ORIGINS);
		const source = fromEnv.length ? fromEnv : STATIC_ALLOWED_PARENT_ORIGINS;
		const list = source
			.map((s) => s.trim())
			.filter(Boolean)
			.filter((o) => {
				try { return new URL(o).protocol === 'https:'; } catch { return false; }
			});
		return uniq(list);
	};

	const getAllowedApiOrigins = () => {
		const fromEnv = parseEnvList(process.env.REACT_APP_ALLOWED_API_BASES);
		const source = fromEnv.length ? fromEnv : STATIC_ALLOWED_API_BASES;
		const list = source
			.map((s) => s.trim())
			.filter(Boolean)
			.map((u) => {
				try { return new URL(u).origin; } catch { return null; }
			})
			.filter(Boolean);
		return uniq(list);
	};

	// CSRF token fetch/cache from API base
	let csrfTokenCache = '';
	const getCsrfTokenFromApi = async () => {
		try {
			if (csrfTokenCache) return csrfTokenCache;
			if (!baseUrl) return '';
			const res = await fetch(`${baseUrl}/api/csrf-token`, { method: 'GET' });
			const data = await res.json();
			csrfTokenCache = data.csrfToken || '';
			return csrfTokenCache;
		} catch (_) {
			return '';
		}
	};

	const isAllowedUrl = (value) => {
		try {
			const u = new URL(value);
			if (u.protocol === 'https:') return true;
			if (u.protocol === 'http:' && (u.hostname === 'localhost' || u.hostname === '127.0.0.1' || u.hostname === '[::1]')) return true;
			return false;
		} catch { return false; }
	};

	const isJwtLike = (t) => typeof t === 'string' && /^eyJ[A-Za-z0-9_\-]+\.[A-Za-z0-9_\-]+\.[A-Za-z0-9_\-]+$/.test(t);
	const isEmailLike = (e) => typeof e === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
	const cleanPath = (p) => {
		if (typeof p !== 'string') return '';
		if (p.includes('..')) return '';
		return p.replace(/[^A-Za-z0-9_\-./]/g, '').replace(/\/+/g, '/');
	};
	const singleParam = (params, key) => {
		const all = params.getAll(key);
		if (!all || all.length === 0) return '';
		return all[0] ?? '';
	};
	const parseBool = (v) => v === true || v === 'true' || v === '1';

	// Parse and validate URL params defensively
	const rawParams = new URLSearchParams(window.location.search);
	const rawSigner = singleParam(rawParams, 'signer');
	const rawFileId = singleParam(rawParams, 'file');
	const rawBaseUrl = singleParam(rawParams, 'baseurl');
	const rawBaseUrl2 = singleParam(rawParams, 'baseurl2');
	const rawId = singleParam(rawParams, 'id');
	const rawEmail = singleParam(rawParams, 'email');
	const rawToken = singleParam(rawParams, 'token');
	const rawDomain = singleParam(rawParams, 'domain');
	const rawSignStatus = singleParam(rawParams, 'sign_status');
	const rawSigner1 = singleParam(rawParams, 'signer1');
	const rawSigner2 = singleParam(rawParams, 'signer2');
	const rawTooltip = singleParam(rawParams, 'tooltip');
	const rawTemplateId = singleParam(rawParams, 'agreement_template_id');
	const rawSignatureType = singleParam(rawParams, 'signature_type');

	const allowedApiOrigins = getAllowedApiOrigins();
	const allowedParentOrigins = getAllowedOrigins();

	// Validate and normalize critical params
	const safeFileId = cleanPath(rawFileId);
	// const safeBaseUrl = (isAllowedUrl(rawBaseUrl) && allowedApiOrigins.includes(new URL(rawBaseUrl).origin)) ? rawBaseUrl : '';
	// const safeBaseUrl2 = (isAllowedUrl(rawBaseUrl2) && allowedApiOrigins.includes(new URL(rawBaseUrl2).origin)) ? rawBaseUrl2 : '';
	const safeBaseUrl = rawBaseUrl;
	const safeBaseUrl2 = rawBaseUrl2;
	const safeEmail = isEmailLike(rawEmail) ? rawEmail : '';
	const safeToken = isJwtLike(rawToken) ? rawToken : '';
	const safeDomain = (() => {
		try {
			const u = new URL(rawDomain);
			const isHttps = u.protocol === 'https:';
			const isLocalHttp = (u.protocol === 'http:' && (u.hostname === 'localhost' || u.hostname === '127.0.0.1' || u.hostname === '[::1]'));
			return ((isHttps || isLocalHttp) && allowedParentOrigins.includes(u.origin)) ? u.origin : '';
		} catch { return ''; }
	})();
	const safeSignStatus = (rawSignStatus === '1' || rawSignStatus === '2') ? rawSignStatus : '';
	const safeSigner1 = isEmailLike(rawSigner1) ? rawSigner1 : '';
	const safeSigner2 = isEmailLike(rawSigner2) ? rawSigner2 : '';
	const safeTooltip = parseBool(rawTooltip);
	const safeTemplateId = rawTemplateId && /^\d+$/.test(rawTemplateId) ? rawTemplateId : '';
	// Normalize signature type: '0' => two-party (default), '1' => single-signer
	const safeSignatureType = (rawSignatureType === '1' || rawSignatureType === 1) ? '1' : '0';

	const [signer, setSigner] = useState(rawSigner || '');
	const fileId = safeFileId;
	const baseUrl = safeBaseUrl;
	const baseUrl2 = safeBaseUrl2;
	const id = rawId;
	const email = safeEmail;
	const token = safeToken;
	const domain = safeDomain;
	const sign_status = safeSignStatus;
	const signer1 = safeSigner1;
	const signer2 = safeSigner2;
	const tooltip = String(safeTooltip);
	const agreement_template_id = safeTemplateId;
	const signature_type = safeSignatureType;
	const isSingleSignature = signature_type === '1';
	const [step, setStep] = useState(1);
	const [tourOpen2, setTourOpen2] = useState(false);
	const [tourStep2, setTourStep2] = useState(0);

	const tourSteps2 = [
		{
			title: 'Add Your Signature',
			description: 'Signature Page: Click here to add your signature.',
			target: () => document.querySelector('.addSignatureBtn'),
		},
		{
			title: 'Use Your Cursor to Sign',
			description: `Use your cursor to draw your signature, then click "confirm" to apply it.`,
			target: () => document.querySelector('.addSignatureDialog'),
		},
		{
			title: 'Place Your Signature',
			description: 'Drag your signature to the correct spot, then click the green check to place your signature.',
			target: () => document.querySelector('.dragableSignature .react-draggable'),
		},
		{
			title: 'Finalize Your Signature',
			description: 'Click here to finalize your signature.',
			target: () => document.querySelector('.finalizeBtn'),
		},
		{
			title: 'Guide On / Guide Off',
			description: 'Toggle the guide on or off at any time. Guidance is always available when you need a quick tip or step-by-step help.',
			target: () => document.querySelector('.tootip-toogle'),
		},
	];

	useEffect(() => {
		if (tooltip === "true") {
			if (signer) {
				setTourOpen2(true);
			}
		}
	}, [tooltip, signer])

	const handleTourStepChange2 = (current, url) => {
		if (current === 1) {
			setSignatureDialogVisible(false);
			setSignatureDialogVisible(true);
		}
		if (current === 2) {
			setSignatureDialogVisible(false);
			if (!url) {
				setSignatureURL(`data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAlgAAADICAYAAAA0n5+2AAAQAElEQVR4AeydCbx131jHT/EPJVOhgQYUDaZSSNGAUEopishcSEkUKg36/8sUElKZx1KmimjOTOYhMofMU6ZM1e973rPuf737Pefee+7dZ5+99vm+n/XcNey91177u99z9nOe9axnf/7MfxKQgAQkIAEJSEACvRJQweoVp51JQAISkEA/BOxFAm0TUMFq+/45eglIQAISkIAERkhABWuEN8UhSaAPAvYhAQlIQALbI6CCtT32nlkCEpCABCQggYkSUMFaeWPdIAEJSEACEpCABI5GQAXraNw8SgISkIAEJLAdAp61CQIqWE3cJgcpAQlIQAISkEBLBFSwWrpbjlUCEuiDgH1IQAIS2DgBFayNI/YEEpCABCQgAQnsGgEVrF27431cr31IQAISkIAEJLAvARWsffG4UQISkIAEJCCBVgiMaZwqWGO6G45FAhKQgAQkIIFJEFDBmsRt9CIkIAEJ9EHAPiQggb4IqGD1RdJ+JCABCUhAAhKQwIKACtYChJkE+iBgHxKQgAQkIAEIqGBBQZGABCQgAQlIQAI9EhiZgtXjldmVBCQgAQlIQAIS2BIBFawtgfe0EpCABCTQEAGHKoE1CahgrQnM3SUgAQlIQAISkMBBBFSwDiLkdglIoA8C9iEBCUhgpwioYO3U7fZiJSABCUhAAhIYgoAK1hCU+ziHfUhAAhKQgAQk0AwBFaxmbpUDlYAEJCABCYyPgCNaTkAFazkXWyUgAQlIQAISkMCRCahgHRmdB0pAAhLog4B9SEACUySggjXFu+o1SUACEpCABCSwVQIqWFvF78n7IGAfEpCABCQggbERUMEa2x1xPBKQgAQkIAEJNE/g82ez5q/BC5CABCQgAQlIQAKjIqAFa1S3w8FIQAISkMAeAQsSaJiAClbDN8+hS0ACEpCABCQwTgIqWOO8L45KAn0QsA8JSEACEtgSARWsLYH3tBKQgAQkIAEJTJeACtZ+99ZtEpCABCQgAQlI4AgEVLCOAM1DJCABCUhAAtsk4LnHT0AFa/z3yBFKQAISkIAEJNAYARWsxm6Yw5WABPogYB8SkIAENktABWuzfO1dAhKQgAQkIIEdJKCCtYM3vY9Ltg8JSEACEpCABFYTUMFazcYtEpCABCQgAQm0RWA0o1XBGs2tcCASkIAEJCABCUyFgArWVO6k1yEBCUigDwL2IQEJ9EJABasXjHYiAQlIQAISkIAEziSggnUmC0sS6IOAfUhAAhKQgARmKlj+J5CABCQgAQlIQAI9ExifgtXzBdqdBCQgAQlIQAISGJqACtbQxD2fBCQgAQk0ScBBS2AdAipY69ByXwlIQAISkIAEJHAIAipYh4DkLhKQQB8E7EMCEpDA7hBQwdqde+2V9k/grOnyKpG7RP4m8sjIByL/15FXpv4vEfa5VfK7Rjjml5P/WOSbIr8ZuU3kzyNvitDHW5L/U+TfIpQfkpzjvyb52SImCUhAAhIYKQEVrJHemGXDsm2rBK6Ys98kgsKDsvPelD8T+efIGZFrRW4cOV+kmy6ZhitH2Acl6fSUOeYeyZ8YeXXkNyIPjPx45CIREorUd6fwrRHKKFccz/n/J20vjVwsYpKABCQggZERUMEa2Q1xOKMi8IMZDRYlrEnPS/nhERQelJ3zp0z6NH8iKDtYqZbJ+7N9E+my6fQNkT+IXDRikoAEJLANAp5zCQEVrCVQbNp5AkzXvSAU/iqCRSnZPL01f7FY3TT55y2EqTrKWJlQvpYJyhj7nJZjvjaCNet7kiPXTH73yG9FnhJ5RORDERLnK2Xqq+R22fDGCApgMpMEJCABCWybgArWtu+A5x8DgXNkEEzjPSb52yNM110+OemP8wflBz8plCOUIpSgNK+dPpsjUJqenRxFDfnblO8WQan7keQob0wzopBxvounjXPeNjnTiW9O/sHIssQUJv5ay7ZNte3quTCmbd+V/JORJ0VqpTjVBpJDlIAEJkdABWtyt9QLWoPAhbLv/SJM/+GIfsOUaUOBQalCsfmZtKH8vDb5NtL7clIUsQclv16EqcAvSX72CGNEAUxxL2FJY0qTMe81TqzAPbp1ronp2Gcmx2r4ZclhgpKKgoWkySQBCUhgOwRUsLbD3bP2S2Dd3n4hBzCdhrWK8mVSJ6HI3CsFFBgUFOqpjjJ9KqNijCiAWLsop2kvYYX799SuGplCQoniGp+ci+G+oXAy1ZrqDIX4nRQWgnLFggEseosmMwlIQALDElDBGpa3Z9seAZSoX8zpse5gtaofvi9POw/vGyUndEKy5hLWLBQtFI8y+Euk8HcRVh8mayoVhYrpPxQqcpTG61RXgQLMfkyl/lTaHx8pCb83fNvYXtrMJSABCQxG4ISCNdjpPJEEBieAYkVoAx7Qv1+dnYfzfVPHt4rVeCgo70i99YSv1rVzEa+JtJZQhlB0UYK5XyhUtDElWK4FP7QfSoXwFEzhMk3436lzP2+QnPuYbJ44jj7mFf9IQAISGJKACtaQtD3XkARYWfecnPBlESw450lOYmUgCsgPp3KHyLZ8q3LqjaW/Ts/fHEEB4Tq7flrZNKpEOAymbHFQ7ypEz8hIsTyy6AALHX5o3EOCsWbTKQkFjYUDZQMKGtatUjdvjIDDlUCrBFSwWr1zjnsZAUIm3Ccb3h0hNtSVkpNYuce0IA9orB8oIFg92DZlwarztBFfIBYmFKIHZ4xM2Z43Of5UWKm+M2XuF8FZuXcvSv2wiQCu9b43ryuWJSABCQxBQAVrCMqeYwgCREfnVTRYpS64OCGxobDg4KODFWTRvEvZaK+VFZv4VmGxQtFioHfKn2+MYKV6bvKjJhRLpBx/uRTwyUpmkoAEJDAMARWsYTh7ls0QuHC6xQKCzw7v9/u+1ElMNTHt9HWpjNmCk+HtXCKMBLG6iDlWLh6/KaxV907DeyJ9JHyzSj/nTIHwDclMEpCABIYhoIJ1AGc3j5IA/jj3z8j+M4IFJNk8sRoQv6PrpoY1K5lpRARQhlGuULIYFv5xOKvTTr1PwU+r7k8LZk3DsgQksCHECKlgbR+wJeiSA4zq+OLzG5uerfglNUFYD1lND1S4Wt0iAuFS807FWhrFafUvGtMpZPZuOlV6So3khdrJ5ImTFvOAfCUyEgJcxcgIqWCO/QQ5vToDpHZbt47j+bfOW2YwHKA/pr0id0ARTXA2YS2s+EegU5Qoli4vBgR2fqE1Yrei/Fiyadd2yBCQggcEIqGANhtoTHZEAihU+VSy3pwtiVeG4Xh7SvIOOdmU4ArwrkftR5FY5NTHGCLWA/FHqbPuV5AQ6TTZPKMQ4sKMczxs2/Od1nf5ZqXhmkyUJSEACGySggrVBuHZ9LAI8oHFeJy8d8VoYHNs34bj+NTnJV0VYcYi15WYps7z/Fsl/NnL7yKMiWGOekvwvI0+I/EWE17f8WfLHRR4bwYEboU7+6LQRigAl5KEpU2e1I+EH6ONP0/bICPGbOJ6I5JwHaw/CufApemr2QYgNxRiK0MY+jIFj6YM+H5b9nx4hLMU9k1NmvJz/1amjnH4g+Ucjn4x8NvK/EfJPJ6eNbR9J+cMRQijwShqOQfEtQiBXfJxQYBDuE9t+L8eUBAeuv9SHyN/WOclPd+pWJSABCWyMgArWxtBOvuNNXiBO0DygyzlQMrB8rBMwE0WJcA0oTrxehbAAWL54FQ4Pe/pkpRnv68Mq9pacjAfym5Oj3KAIoaQQpPIn0kYfKGAXSJmgpbyv8MtS/tII8Zs4F9OVX5k6YQcQ6uz3ubSxSu605JRRHIukaUYbcbmI48XxX55GzkNoAYQ+vjht517IOZIzhiK0sw9j4FgWAaAc3jT7wfLbkxN49OLJrxHhmvBZY/xYo1hld/a0nyXCOMkZK21sO1faOQfXybZUT0rEGcP3DQXspA1VhXMOMS1YnXL23rqSMuNMZpKABCSweQIqWJtn7BnWI8B0EyvNylFYdu6cygsjKAhXTI6F5JbJsZBgVWJaCusPca9wmuZBiqL0quyDFQnrE5YVlBf+z7MflqO7ZTuK11WSXySCctEVLGjHkWumX86NFQwnfa7vxmnjNT1YfVDeuB62sQLyOOcqx7Iyr1wHShfKGgE7L5rzojCVbevksEPBRClDmWOs5XiUWeq0o7g9M+dZlrherGjLtm2iDcV5E/2u6vMR2fDxCKtbud8pmiQggeEJjOOMPGzGMRJHsesEvigAXhlhuinZPOGr8wMpoXBhXcKn5nmp4+ODJeoKKfPQZwqLlYWsUvultH1vBIUJxeI7UkbxuFpyVhuilGFJYUoNC9ZL045ShgUrRdMKAv+VdgKDJpthqcJiRbkrWNe+f9HItCaWx0V1nqFwYmWbVzb8B2WwPsXz60rPZSyWTEF+YfplGhslOkWTBCSwqwRUsHb1zm/3urGqsDLwTzIMfIJ4eH8s5UtGSPj/4FeE7xDK0I+mEStTsZiQE0QUxQnBQsV++B7hl4T1SoUp0LaQUIbLaXktEVOxWBRLGzmvwSHftDBFW5/jP+pKz+Wf6/SHpRXpNLdRdZQSkMDxCahgHZ+hPexPAEsSD1osR1ifeNiiALEy8BtyKD5QKFIpzhPTOvg7IShND0wr1pJ/TW4aN4Hab44pwzLaF5fCIkcpXhQHzfh/tIkT4uPGislu3/idddusS0ACO0JABWtHbvSAl8m0HEEdCQSKLwrTb/imfFfG8OwIMavYBysUloxfTxv7JJsnfHRYJTevtPlnJ0eNMlwUJ6Zwa2WmLgPnsvwZQFDmy2nWWSBRjjlMzoIC/APLvq8oheS3ibScWADBIhGEe8uCiZavx7FLYFACKliD4p7cyZimw9fk+rkyYhzho0MIAJzScXz+7bTjcM0X9ZVS5lc+r7DBETjVvVSmBmn4Av4oTRHgvY/4vzFoLJC3plAJSnNVnQ3lg1VHckepr8dw3DIvpeZ1Tfj+lb5YfdpV5LallFw6g+KzyX1B0WPK/R/TxgIEFoTgH8d4Ce2BbxqhN96f7fhBEsKDVa60MdWOYJ1k+pd2csJ1sLgExZoFJyygyOEmCUigEBilglUGZz4qAt+X0bAKjC9kXleDozM+Lazy45UnxEviyxYHZ1bNsS9hDmrrVLpYmv6+auWXclW12ACBu1Zj7CpXbEKh/hCFhdSWpUXTRrL3Vb3WlqWqee0iCyuYtn5OjsRKm2ye+FFxl5S6bxQYSsFCwYE9MdmIZUYUez6bfCYZL9P0TNtePWPkM4pSTLw3FiFwTfwIYhVoHYaD+4T1ET86cuooYFwT/m18zlHgUCpZmYvyxbmw6uU0JgnsNgEVrN2+/6uunhV3/OJF8eFLlS9OygSK5AuZ19XgO4XV6uvTCZapeycndlSytRMKWzmoTEeUuvm4CfAAL87chJuo/enKyHl4E0Or1InpVcqbzJmW7qt/VrNi8cHag0Wovh5eLn6PnAjfQj4vKe6ly+yV+i+g5Nwp3bKwAwWHVbL4LjL9h3p5PAAAEABJREFUjvWOqfa7ZzufU1bdomCtEn4ksfqRMBvETKMPrNDsz2eenDqWMc5LG4oVVi8C0+Y08wQbrHooevOGif3xciRwaAIqWIdGNdkdcdAlHhRTB1ihUKaelavlFy9Wq69OmYQVAqWLX8AsxWfKj4frG9h4TOFX/2uqPrRiVTBGXOQ+YalkiCgW/L+h3JXulCAWkO4+m6gTNqH0iwWmlA+bc31YaPhMoDhi8amPRdlC8WDBRmmHA+FFSp3wDaV83Jzva9ixYpFpPnwcidBPAF3CmBBbjRW3BKDlM0rsMz7bfE7vlZPDYJW8LNuZ3k12YOINAFi1UKgJhMuY4FBPBaPoEeiVqdQDO3QHCUyRAB/YKV6X17SaAPGmTs9mgnDy4MBBl1+4KE4EocymvcQ0IL9Er50WHpJ8afJuOX4dp6nXRLiG0uGPpcDDLZlpxAS4R0R5Z4hYSFAuKK+WE1t4yJ8obe4vq1exhnIGfjjwoEeoLxOuBUWEhRhYg16fnfA7oi3FU9ID0sLU6LJrIcxINs9TH9Y6Ymsx/UgcOPyfODeWJHyj4E4IE4Lw8volPq/EhZuffKA/TBvCASsfSmhR1FC++OFEzDn4DjQcTyOBcRBQwRrHfdj0KPilyYMDhYp4UzwYmAbonpcHET5WBOtkpRc+Gfwi5tc727r791lnpWHpj2kKphpK3XycBLB+lpFh0Sjlbl4UndK+375ln+PmBKAtfaDk4BvEwx6lhGC2KE9FaKOMtYqVrfgzMfVdjq9zYrYxPYb/FUpFva2U+fFSyt1rL+2Hyflhw7Qj1uMzcgCLSj6VnKk5rFPUsUwRwoT2bNp64ocSU401G75DEHy/tj5AByCBoQioYA1Fup/zrNMLrzbhQYJSxbJ5HhzLjkdxQqnCasQXI69uwdcKJ9ll+2+qDesHfiylf34JI6VuPi4CWCSIys6o6ocp9a50lYy+V/R1z0edMb2bQkcYC68rYvxFaOvsdkqVaT9+qBC77SAFcZVydkqnSxrOmjY+fywgeVrK+D0lm+EDebMUmPpjHEzppTraxJQhK4bLAHkVFNOarDwsbeYSmDQBFaxp3V6m+HA458sZMz0PkmVXWJQqpvx4nQhKFWb8oacWumPjCxgrQ2nHqXjVFE3Zx3w7BFBOypl55VApL8uZzqrba2tl3d53eT8r037nYgoc5QwljelzlIXL5QAsR7yUO8V9E1NjZYdVfmlle8l5sTYx4Vj5iF8b1ilWXvJicnycWHiCcsIPpnLM2HNWKsKvHicrD3Ez8MdTTWUSZS+iS0AFq0ukzTpK0n0ydBQnIqbz5ZzqKQn/DRxi8ZtBqcJp/ROn7LW9Bh5orEasR8C0Db/o6zbL2yeA308ZBfetlJfltVLCqjOslcv267sNSxPKERazm6ZzFCQUeKxRqe4lpuG4BpSBsj8+h5RR8Nm2t/MhCihJZTccwkt5VX7VbCCuFHHjcFBPdYafFaszb5QK78tM1mSCH8ppzZzr9cdTk7fTQa9DQAVrHVrj25eXGhM8kC9xHFyXjZAHGl/cOMESXoFph2X7jaUN6wY+LvV4+EWPj0xtNam3Wx6eQLHSoHwg+40AH6eyHetqKQ+Vo/Dw/4qpNXwPeeCjPCGEI8DnijLKANfCD5XjjK3+f7qfYz3n4McDFp3zUYngw8TKXaxvONqnqfmEcgXz7ueaH0+sfGz+Ar0ACSwjoIK1jMr423AW5UHwDxkqD4ZkpyQCK94grfhV8UW2jQdbTn+kVCwPPBhLBzy0mEK8fWkw3xoBfJZYiMAADmON4v8f+yL1Cjvq2xI+P0jf5+f/ad0nvlN1vZR5ewF+VPx4oA3neaYBmToj/AJtUxMUSayE9XXxecaaRay0ut2yBJonoILVzi0kvALTBfwaRNGop2jKVTAV8+BU+NVMgMPHp4w/SbLmEg+/R3VGfbbU+cW75KGVLaahCNSWCCyoQ523hfNgVa7Hyee1rlPG95HpeT6j1AmtgGVt6v+vP5yLxUqIcl5zQalkIU42myQwHQIqWOO/l5fIEH83wnQBCgcRl1M9KfEOQMIasDSdnCjrJ+3QaOWOGTe/eLt+YgRAxZ8M35rsYhqYQL0itX5QLhtG16JzkEP8sj5aaqtDVxA6gff61ePHYoNCxepAfhDdPBtZwYtDe4o7kQgszJQhP5bKBaNkdVmVbeYSaJLAnoLV5OinPWi+qHFcR1niPWeEXehe8V+l4RoRloVjudr2KsAMpffEL15i6DCdUnfOyioCQt62brQ8CAHiM5UTHaTkFl+tsj/TQaU8xRz/qXJd+D+iZJX6w1LgM51sxtQqi1Fo492Bsx38h98oiw/KpWPZYpXkpUqDuQRaJqCCNb67RwRqlAoirPMFtGyEvESZLyNeZ8MrblqdBlx2bcvauMafzYbnRupEAEleFYJ0H+T1fpY3R4Cp3P16xzJRtrOCD+tFqU8t5/8gFudyXUzll/ITUkCZ4DsXXyQsOLxKJs07nVh8wEIDrPMFBP6jRIXn802bIoEmCfBhb3LgEx00ihUR12un4HKpn0zhoRGWj98y+ZQfVLm8U9KL0sJUyp8k51dusnniNSJ3Sgnlq+v/kmZTzwRwcC9dYoUp5VV5rWCxQm7VflNox5pcXwfKJz8MPpPG60dKwpkd6xaf99K26znvjfypCgJvj8DdoTvFXO1iUQLjJqCCNZ77wxcJXyhf0RnSB1Kn/YLJbxEhAGKynUxc+61z5XePdKdVmG5hVSWK1rIFADlkR1O/l83rk0qP/N8s5WU5U9d1O1PadX1q5XrqlGvDf5Cpe6KzU+8KP6TwvaqV0O4+u1R/bC4W3zSs9ynOiOmHlZ6yIoHmCKhgjeOW8QVLnCemGMqI+PWLfxGv5uBL+rixeUq/reefywXwYMJHbZmD9XdkO6uxTk9e+8OkauqZANbU/bqs+ROrbcpOzAQFxVew5sF0f11fViaw6K9mw8UiptkMq+gVZrMZ33+z/COMBQtaastpmk0SGD8BFayD79Gm9+CLA4WhnIfYT6yuIb7Vg9JIPZmpQwDl6vJpY5qldiRO0wyLwV1TeF0EtkwjpmjqgQDcSzdYW7G8lno3xxpR2ngvXfc+lW2t5iiY/P8jkOrzchFniXQTKwWfnkaisROLDsUBIe5VmueJVwmdkRLfBclMIcD3H4FhU5yxoOVxKez3fy2bTRIYFwEVrO3fDyI247BeRoJStcq5vexjfoIA1iymYZi2emCaulY+3u/GAxDrCU7F2cU0IAEejOV0TN2W8hRyrM74BaLAr1KMiBF2oVwsDtuw4G0KKA4IygPhVbJ5nujvJvOSfwoBXm3E55s6FsJHpwCnZKYTBPw7ZgIqWNu/O5jAyyiIYH6/UjE/NIGPZ8+fixAd+9eS46uVbC/x7kUsDAReZZ+9DRaORAALTDlwP6vCrcpOyZn6STaJhNJOuAmUp1UXxMpXFmV0lf6yP07cxK3DB6u0oaypQBQaJ3JYF982eMOdNlcYnuDj3xETUMHa7s3pPpxYyk204+2Oqt2zvy1Dx/eKmGFYCQhnkaZ5Oi1/ecE1Dz5WLKVqOiKBWllaFWqgdk5mmrtWyo542v4PO0KPPNxRhPY7FJ+hH84OtfKU6tKEJaveoIJV0zhRRhmt3x4A/+dn00UjJgmMloAK1nZvTf1lykPrSdsdzmTOzgpDHuiEs2BaBqWLhzwXiN8MsXf40qaurE+Ah1s56kql0Ml/pKpjma2qzRb5QbTqestF8R5BwjAc1t8MNvxfLcfzncCrrkrd/AQBOBEvi5WGtOBWQaDh26XCfUlmksC4CKhgbfd+1JHXeddgY18U24V3yLPjWMy0ISva8Hcrfi/fn+PfGfm2iGk9Auetdl+1+g2/o7IbD8dSbjXns4lF9KrVBdSO6jQT8BcFbF0rNP8vOb4I5ypl85MJECvrF9KEW0Cy2R/kz70j9f+3VE0S2D4BFazt3oNnVacnRAOhGgiiec20owAkM/VEgECtD0hfLJ0n/AVWGFbB4aj88LTjKJ/MdAgCdUBNwmV0D4Ex/59pR8GtLTS0tSh8HuspKfz8sKjU10LA23fUDYcsY4l5ebUvVqyqarFDAKXqB9NW4mWxgACL9GPSNnTi/zkuCeQoxoxlvzFgUWd7vT9l2pQ+CYygLxWs7d4EHjx8SXd/4bOs+48yNN6/x/TWQe97y66mQxL4bPZjpeaVk/Ml/YbkrN4isjYWinOnbtqfQJluLXtdphQWeXmIUJ3CdwxhPlCwuJ4ivCOU6eZS/9cUWMmabO2E8s8bCsqBPKhL2Xw5Ab47iZdVVhmy1w3zhx+o/GhC2YJjEVZ6ovwQtJnPO0LQYpRZFmPcI8fiOsCxT06ZH7sIIThK/vq08z3BjwbaEXwQUarJ2Q//O8aA8F1DO2X2ZRuhY6jTXvanTN8o2rxeifYi/Ph7SM5LjjD+86VuaoDAFL78GsB84BBx4ETRwjGbLwwUgE/nKB5cxHN6T8p88IiXk6KpJwLEZoI5yixd3jx/iE5eTwOlydQhUKZnSjPBXUuZHIWBHOFBQd6y/HIGf9lISbzOqv7Rw6tw+Nzy4Cz7rJvzma9jjH3nuh30vH8r3bHoAPb1eJn2R9ni/16RouCgBKOoIDBnVSIKDPeYxS8ce510VitmlFHQeDMBlm7eGkEdya4rE3HRinWKfVHwVu1M31hIUeQ5XxEUQRRAcoTxM8Oxqh/bR0RABWtENyND4VcZXxhMYWEFQHi9CE7bfPCYRuDXE9aX7G7qgQB+WPySZdUXqxD5UnxG+mU6kS/TFE0HEOBhUO/CKq9S52FUyq3m+P2UsTMthUKOk3Vpw9L8L6VyjJxpx3L4fg/jso/5CQJ8Z/IDlR+qXevqiT38K4EtEFDB2gL0NU6JMsVydxQtvtRZ9s0Diy9zlC2/hNeAecCuT8t2Hpq/mxzrIXG1CI7JFEKa1kzT3p1f4/UVovxPddqC1ZBYFsr1ElH8V0olOdNAWBUIepvqsRI+XKUDrRSFxOFzXC2wLHK/sGoh5egPpoCFkNXa/JBlOhAL9kPTTpkfssTJ4wcX+6R5niizPwsaUIDphylB2pH5Tvv8YeHDPptP2kRAZHxDSyPnZWwEXCVH+PHND8Cyj/mICahgjfjmVENjbh4rC87DfLmzEo7pQub02ca7zKrdLR6RAFNfTMny/kemZFFsmUJ4cfpDsU1mCgEYJdtLBHJ95KLG/1EU1UW1+ay2FvO54//Bpaqrwur5lKp+nCJ+Xfxfow+mqVEWKCvrEXhzdseqhWDZQrBG8zYHXt+EW8BNsw8+mLdITpkfsjdImWCm7MMxCGX2x5Ednzv6uXD2ox3hHrEd6xkKHXHNKHMujieYLNvZl3qRchzbaGMqkUU3TLdTR9jG2OiTHGFaEyUvQzCNncBYFayxc9vW+Fj+fc+c/JsjN46gBPDB/Z2U8f/gfWeEJDhH6qajEyCWERYErFc4tfJliTXr/i0R5/MAABAASURBVOmS9xwm2+nEr2ysATUEHlY46qL087Ao23gglHKLOQ/LMu6/SIFXtiSbYZlg6h6rCe8bpK0P4Rz0w1Q1D3zKyngJMCWJpYn/Byh0WJsol89H2d61dpV2juXq+J4hVyZEQAWrzZvJFzrv5UIJwFrAtAXWFxxx755L+lgE5evXk2ONSWY6AgEedvBjhdd5cjzxiphCICJ8qjudisWqhoCjbh0ji9fEoIzV+7RUxrcMq0IZ81VSYFr+NcnPFuH7swS+TLWXxPRPWSTAq3Z66dRO+iRgXxI4HAG+IA63p3uNlQDLflkxQ3gBTMr4FGDNwi/mtzNoLApMI94+ZX59E9A0RdMhCfDLklU8PGxfm2Mw7fMr9R9S/pbIriYWAeATsur6mab5/WzEXyVZkwmFsQwc/0fuN5YIPnNM4bwgG18d6TO9Kp3x3sxkMz6vBMilrEhAAo0RUMFq7IbtM1ycbDE341PwBdmPL2emZ3CU58Wo900bX9zUcZi/Wep+eQfCIRPs8L25Y/bHsfl7kzMNgE9cijuZ8Alh6ThTp1h1+P+H4ItCmAGmTE4B01ADfo5luMUyh08e/jp83u5XNvacFz8svp9xsu+5e7uTgASGIMAHeIjzeI5hCeAbwq9rVpwwbXiJnB5FgOB750wZp0ksXfwaJ8YWlog7p51QBSpdAbEi8VC9T7bh7MrKsRRnKBFYMXgn2mwH/zFViHUUv0AsqAhMWBHVOo7uOwHxrcHCdFoujHhpKJMp9p549QtuAHT8k/xRJCCB9gioYDV3z440YKY3cI6/UY5mKpGVKkzfEO6BbQTYIzwBq6FQuhB8uHCYJ/jmBXOc6UwCn0qRMA5XT870K9OGvL6DYIYoF/jpZJOpcQKE7iiX8JEUHhXB7zHZjBV//Dih3LegvBGGhX55FdFUQ2BwfYoEJktABWuyt3bfC8Px+JeyB35FWLd4Ye8dUsc6g9LFsnt8uHCY5/UxxH9hiforsg9RjwkZgfP3rq+o+7vwuGSEqVimyIgPRZBN/N6YSmKaNptNjRJgWp0FJCjRd8k18L47PjMpzv6ePxsU4jKV7q9dCuYSGDUBB3cSARWsk3DsbOVNuXIeJvgX8QDhF/PZ08aqKaa+8NnC0oUPEg7fKBQ4fKNgUMaaw2t98PXKYTuXsFoRL4mpV5RXABDiAb8tptBgSpvSHgEWkBD76sEZOp+LsqqQ1+WkaWPpL6uer1SVLUpAAo0QUMFq5EZtYZhMg/EC2z/MubFYoVyxcorpQhy8n5B2lpOjPLCijNeFEO0YZY3wBr+Y7bzXK9lOJIL/sbqQ6VecvMtFE68Mp+hSN2+XANbKMnoWi5RyX3ndD7HYmCqkjf9T5IoEJNAQARWshm7WSIb63oyDF6jifHutlHEEJhAnr/l4auo4gl83OT5eL0qOwz37MwXJg2IXwkRg0eL6c/nzhLKJzCv+aZbAl1YjJ1BkVe29yOfomYteiXVHHLZF1UwCEmiBgApWC3dp/GMkXAErn3gpLg8DrFo4yOOnQkgD6vh3EQ2dIKj4cuHfhdM9Eav7ucJx9YKPG347ZVQoXHAodfP2CHzhYsifSM7/62QbTc+qemc1cFW1KAEJjJ2ACtbY71B74/tMhoyj/OnJrxYhJhc+LPgk4TBMxHmmG1HAWJXFg4qI30SmJ0I6wRzx/8qhzaffyxXgo5Zsnlgg4GuM5iia/IM1dsiB/2N1Ml6JVVUtSkACYydQK1hjH6vja5cAq7Dwy8Jh+EtyGTiEExCVX+g8tIjNhfXr8dmGNQzfLqYXz0idQJb4gOH/grKWpqZSsXowaCKDM6VKWWmPQPm+5P9hKW/yKngtEyt4OcdF+aNIQALtEBjiS6IdGo50CAI4z7MCi0CnhIJgipCVWcTbwjn8yRkEDxUc5Jlie3jqWIFemRzHX5Q19vnV1IkkfpvkxAoi0GWKgyWm+xD8rVACsU4RnoHXFBEPC78zhG31oC5fVyw3ReBti9ESngQla1HdWMaPDwKbcoKL8Gd3xSuXQHsEVLDau2dTHDFRvx+WC0NZ+dHkRErngUJU8GukfrcIQR9ZpcgU23VS/50IxxBR/YUp8yBCuUFQxAiU+tJFO/vRN35iCMfSJ9OUhKbAQZ+l8AQIZTqTaUqUJ5Q3FCQEZQllj6jtWBaoI8S9op3wFRyfU86Ih8XxyKzzj4UAnSarjRAo0dXxI0T5GWLYWHI5Dz9CyBUJSKARAipYjdyoHRwmViBeRcJKKhzieY0Pis+FwgKLFRYvfJywhqFUpXkvXTglrGM4BmPZwtKFIoTjOYL1C2sZ/d4r+xLl/jnJsUCx/J5pSpQnlDeUJwRlCUULJ/6jPuwem3OgnCUbNnm2XgiU70umtHkjQi+dHtAJEePZpV7BSF2RgARGTqB8YYx8mA5PAnsEUKZ4GS5WKaYQ8efi/zFBTlGCCB+B4kTUbZztsY7tHbyhAq8WIvI2PmVY07C0oRwSnJVTPil/sIbhZ5aiqVEC5X4y/KHCJvB/mNf04OTO/3POrUhAAg0Q8AN7qJvkTg0QYNqGhxEBUJn+4/UiKFxYmwiQWoRo3Ew97ie8BggL121z3URnf2JyFCYsYdSRcjz98vAjmChWsytk36+MsP3iydlOXDAigadqaphAmSLkEs7LnwHkHTkH7zwkNASWs1RNEpBACwRUsFq4S46xTwJYIVCW9pPX5YT4bOFcT3T266WOwvSI5NSRcnyaVqZNB6NceWI3bIQA4UQIQ0LnlMmHEKywrL7lLQpDnM9ztETAsY6WgArWaG+NA5OABEZGgOjqWCQZVh1+g/omhdhx9G+IDygoEmiEgApWIzfKYUpAAhshsE6nKFiEaOAYpqDJhxCmvvE9VMEagrbnkEBPBFSwegJpNxKQwE4QKNO+vJNzqAvG/wrL2bmGOqHnkYAEjk9ABev4DHe7B69eArtFoEzXofQMdeWED8GCZTT3oYh7Hgn0QEAFqweIdiEBCewMgQ8srpTQHIvixjOUOixYvgVg46g9wZQIbPtaVLC2fQc8vwQk0BKBsnqQ90oONW5eD8W5Tssfv7MDwSSBFgj4YW3hLjlGCUhgbASuOOCAePk57/C8QM5JQN1kQyTPIQEJHIeACtZx6HmsBCSwqwR43+SQ145z/VlyQl+ZEwgmCbRAQAWrhbvkGJsk4KAnSQBFhwvbliXpizi5IgEJjJ+ACtb475EjlIAExkPgMYuhfGvyIeNS4eSeU85YTUiuSEACIycwYgVr5OQcngQksIsEXlhdNK9QqqobLRbL1Qc3ehY7l4AEeiOggtUbSjuSgAR2gAAvXn7m4jovvcg3nZ07JzhPhPRh/ihbJuDpJXAIAipYh4DkLhKQgAQqAi9YlL87+fkjm04lgvvbcyJiYiUzSUACYyeggjX2O+T4JDA9Aq1f0W8uLgCr0m0X5U1mV150PmRw08UpzSQggaMSUME6KjmPk4AEdpnAPy8u/qcX+Sazb0rnvGj60clNEpBAIwRUsBq5UScN04oEJLBtAvfLAJiyIy7V1VLeZLpUOmf14KeTmyQggUYIqGA1cqMcpgQkMCoCT81oLhw5Z+SekU0mXstz1pzgHRGTBEZNwMGdSUAF60wWliQgAQmsQ+BZi50vk/yakU0kApoSwZ2+X88fRQISaIOAClYb98lRSkAC4yPw/GpI16nKxyiecuhH03KRCOEZtGAFhEkCrRBQwWrlTjlOCUhgbASKozvjulX+ELYhWa/p6Yve6nMtmswkIIExE1DBGvPdcWxrE/AACQxIAKUHKae8TQqEbkjWWypTj4/rrUc7koAEBiGggjUIZk8iAQlMlMATq+v68ZRvH+kr/fmio5ckr8+TqkkCEhg7gY6CNfbhOj4JSEACoyLw5M5ofiL1i0WOmx6TDlDYks0eyR9FAhJoi4AKVlv3y9FKQALjIvCuDOcRkZIunsIZkeMkIsXfcNEBlqsHLMq7nXn1EmiMgApWYzfM4UpAAqMjcHpnRFieHtJpO2wVZ/nfWOzM1OD1FmUzCUigMQIqWI3dMIcrgSMS8LDNEXhjuv6tSJ1QlFC06raDyqxCrBWzOx50gNslIIHxElDBGu+9cWQSkEA7BJjWu29nuA9MnfZkh0r/VO2F31W9QrHaZFECEmiBgArWYe+S+0lAAhLYn8AdsplpvWTzdP78JXTDYZSsWrl6dI67ScQkAQk0TEAFq+Gb59AlIIHREbh2RlRbnlCy8KnaT8liahDJofN0u/lf/0jgkATcbZwEVLDGeV8clQQk0CYBVhXeP0Nn9V+yvXTzlJb5ZF0/7Visks3TXfP3IxGTBCTQOAEVrMZvoMOXgASOS6D345+SHh8U+WSkpAulQOBQpgKxZhGZHUXqCWlnW7LZa/KnVrZSNUlAAq0SUMFq9c45bglIYMwEmCa8Zwb41kidmApkyvAn03iuSEmsRLxzKr7QORBMEpgCARWsKdzFLV+Dp5eABJYSwFKFP1Xt+L5sR5SwW2bDX0dMEpDARAioYE3kRnoZEpDAKAmgNF0uI2M14ZOSl/ShFF4ZeUbkahEsXslMEpBAjwS22pUK1lbxe3IJSGBHCDw413ndyAUinxfhlTqXTn6tCNODyUwSkMCUCKhgTeluei0SkMDYCbxvMcCSL6ojzRyWBCRwZAIqWEdG54ESkIAEJCABCUhgOQEVrOVcbJVAHwTsQwISkIAEdpSACtaO3ngvWwISkIAEJCCBzREYt4K1ueu2ZwlIQAISkIAEJLAxAipYG0NrxxKQgAQkMFUCXpcEDiKggnUQIbdLQAISkIAEJCCBNQmoYK0JzN0lIAE+CNiHBCQggWkTUMGa9v316iQgAQlIQAIS2AIBFawtQO/jlPYhAQlIQAISkMB4CahgjffeODIJSEACEpBAawQc74KACtYChJkEJCABCUhAAhLoi4AKVl8k7UcCEpBAHwTsQwISmAQBFaxJ3EYvQgISkIAEJCCBMRFQwRrT3XAsfRCwDwlIQAISkMDWCahgbf0WOAAJSEACEpCABKZG4FQFa2pX6PVIQAISkIAEJCCBgQmoYA0M3NNJQAISkMDRCHiUBFoioILV0t1yrBKQgAQkIAEJNEFABauJ2+QgJdAHAfuQgAQkIIGhCKhgDUXa80hAAhKQgAQksDMEVLDWuNXuKgEJSEACEpCABA5DQAXrMJTcRwISkIAEJDBeAo5shARUsEZ4UxySBCQgAQlIQAJtE1DBavv+OXoJSKAPAvYhAQlIoGcCKlg9A7U7CUhAAhKQgAQkoILl/4E+CNiHBCQgAQlIQAIVARWsCoZFCUhAAhKQgASmRGB716KCtT32nlkCEpCABCQggYkSUMGa6I31siQgAQn0QcA+JCCBoxFQwToaN4+SgAQkIAEJSEACKwmoYK1E4wYJ9EHAPiQgAQlIYBcJqGDt4l33miUgAQlIQAIS2CiB0StYG716O5eABCQgAQlIQAIbIKCCtQGodinOMX9mAAAAk0lEQVQDANFyXOteKSAEAAAAAElFTkSuQmCC`);
			}
		}
		if (current === 2) {
			setSignatureDialogVisible(false);
			if (!url) {
				setSignatureURL(`data:image/png;base64,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`);
			}
		}
		if (current === 2) {
			setSignatureDialogVisible(false);
			if (!url) {
				setSignatureURL(`data:image/png;base64,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`);
			}
		}
		if (current === 3) {
			setSignatureDialogVisible(false);
		}
		if (current === 4) {
			setStep(1);
		}
		setTimeout(() => {
			setTourStep2(current);
			// window.location.reload();
		}, 300);
	};

	const styles = {
		container: {
			maxWidth: 900,
			margin: "0 auto",
			marginTop: '10px',
			paddingTop: '50px',
		},
		sigBlock: {
			display: "inline-block",
			border: "1px solid #000",
		},
		documentBlock: {
			maxWidth: 800,
			margin: "20px auto",
			marginTop: "0px",
			border: "1px solid #999",

		},
		controls: {
			margin: "0 auto",
			top: '0px',
			zIndex: 9999,
			left: 0,
			margin: 'auto',
			textAlign: 'center',
			paddingBottom: '10px',
			paddingTop: '10px',
		},

		controls1: {
			margin: "0 auto",
			top: '0px',
			zIndex: 9999,
			right: 0,
			margin: 'auto',
			textAlign: 'center',
			paddingBottom: '10px',
			paddingTop: '10px',
		}
	};
	const [pdf, setPdf] = useState(null);
	const [origin, setOrigin] = useState(null);
	const [pdfLoading, setPdfLoading] = useState(true);
	const [autoDate, setAutoDate] = useState(true);
	const [signatureURL, setSignatureURL] = useState(null);
	const [position, setPosition] = useState(null);
	const [signatureDialogVisible, setSignatureDialogVisible] = useState(false);
	const [fontSignatureDialogVisible, setFontSignatureDialogVisible] = useState(false);
	const [textInputVisible, setTextInputVisible] = useState(false);
	const [pageNum, setPageNum] = useState(0);
	const [totalPages, setTotalPages] = useState(0);
	const [pageDetails, setPageDetails] = useState(null);
	const documentRef = useRef(null);
	const [userdata1, setUserdata1] = useState({})
	const [userdata2, setUserdata2] = useState({})

	const [confirm1, setConfirm1] = useState(false);
	const [confirm2, setConfirm2] = useState(false);

	// Signing confirmation modals
	const [ackOpen, setAckOpen] = useState(false);
	const [finalizeOpen, setFinalizeOpen] = useState(false);
	const pendingSignActionRef = useRef(null);
	const latestPdfBase64Ref = useRef(null);
	// Manual fallback: store previous PDF so we can revert if user cancels
	const [preModalPdf, setPreModalPdf] = useState(null);
	const [manualFlowActive, setManualFlowActive] = useState(false);

	useEffect(() => {
		const fetchSigners = async () => {
			try {
				if (!baseUrl) return;
				await axios.post(`${baseUrl}/api/getsigners`, { sign1: signer1 && signer1.toLowerCase(), sign2: signer2 && signer2.toLowerCase() }, {
					headers: token ? { Authorization: `Bearer ${token}` } : {}
				})
					.then(response => {
						const result = response.data.data;
						for (let i = 0; i < result.length; i++) {
							if (result[i].email && signer1 && result[i].email.toLowerCase() === signer1.toLowerCase()) {
								setUserdata1(result[i]);
							}
							if (result[i].email && signer2 && result[i].email.toLowerCase() === signer2.toLowerCase()) {
								setUserdata2(result[i]);
							}
						}
					})
			} catch (error) {
				console.log(error)
			}
		};
		fetchSigners();
	}, [])

	const request = async (...data) => {
		try {
			const response = await fetch(...data);
			return response;
		} catch (error) {
			// Handle any network or fetch related errors here
			console.error("Request error:", error);
			throw error; // Optionally, rethrow the error to propagate it further if needed
		}
	};

	// Compute SHA-256 hex of a base64 string (without data URL prefix)
	const sha256Base64 = async (base64) => {
		try {
			const binary = atob(base64);
			const bytes = new Uint8Array(binary.length);
			for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
			const hash = await crypto.subtle.digest('SHA-256', bytes);
			const hex = Array.from(new Uint8Array(hash)).map(b => b.toString(16).padStart(2, '0')).join('');
			return hex;
		} catch {
			return '';
		}
	};

	const createSignatureImageFromFont = async (text, fontFamily, fontSizePx = 160) => {
		const safeText = text && text.trim() ? text : "Your Signature";
		const fontSpec = `${fontSizePx}px "${fontFamily}"`;
		try {
			if (document && document.fonts && document.fonts.load) {
				await document.fonts.load(fontSpec);
			}
		} catch (e) { }
		const padding = 24;
		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d');
		ctx.font = fontSpec;
		const metrics = ctx.measureText(safeText);
		const textWidth = Math.ceil(metrics.width);
		const ascent = Math.ceil(metrics.actualBoundingBoxAscent || fontSizePx * 0.8);
		const descent = Math.ceil(metrics.actualBoundingBoxDescent || fontSizePx * 0.2);
		const height = ascent + descent + padding * 2;
		const width = textWidth + padding * 2;
		canvas.width = width;
		canvas.height = height;
		const ctx2 = canvas.getContext('2d');
		ctx2.clearRect(0, 0, width, height);
		ctx2.fillStyle = '#000000';
		ctx2.font = fontSpec;
		ctx2.textBaseline = 'alphabetic';
		ctx2.fillText(safeText, padding, padding + ascent);
		return canvas.toDataURL('image/png');
	};

	// Convert a data URL PDF to Uint8Array for pdfjs parsing
	const dataUrlToUint8 = (dataUrl) => {
		try {
			const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
			const binaryString = atob(base64);
			const len = binaryString.length;
			const bytes = new Uint8Array(len);
			for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
			return bytes;
		} catch (e) {
			return null;
		}
	};

	// Defensive check on PDFs to avoid script/JS actions where possible
	const isLikelySafePdf = async (bytes) => {
		try {
			if (!bytes || bytes.length < 4) return false;
			// Quick header check: %PDF
			const header = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
			if (header !== '%PDF') return false;
			// Basic scan for embedded JS markers (heuristic)
			const text = new TextDecoder('latin1').decode(bytes.subarray(0, Math.min(bytes.length, 200000)));
			const lowered = text.toLowerCase();
			if (lowered.includes('/javascript') || lowered.includes('/jsaction') || lowered.includes('/launch')) {
				return false;
			}
			return true;
		} catch {
			return false;
		}
	};

	// Smoothly scroll the window to a position corresponding to PDF units on the rendered page
	const scrollToSignaturePosition = (pdfX, pdfY, pdfPageHeight) => {
		try {
			setTimeout(() => {
				const pageCanvas = documentRef.current?.querySelector('.react-pdf__Page canvas')
					|| documentRef.current?.querySelector('canvas');
				if (!pageCanvas || !pdfPageHeight) return;
				const rect = pageCanvas.getBoundingClientRect();
				const pageClientHeight = pageCanvas.clientHeight;
				// Convert from PDF user units (origin bottom-left) to CSS pixels from canvas top
				const targetYFromTop = pageClientHeight - ((pdfY * pageClientHeight) / pdfPageHeight);
				const targetWindowY = rect.top + window.scrollY + targetYFromTop - 200; // offset for nicer view
				window.scrollTo({ top: Math.max(0, targetWindowY), behavior: 'smooth' });
			}, 400);
		} catch (e) { }
	};

	// Ensure we always produce a base64 string for the current PDF, regardless of source URL scheme
	const getPdfBase64 = async (src) => {
		if (!src) return null;
		try {
			if (src.startsWith('data:application/pdf;base64,')) {
				return src.split(',')[1];
			}
			if (src.startsWith('data:application/octet-stream;base64,')) {
				return src.split(',')[1];
			}
			if (src.startsWith('blob:')) {
				const resp = await fetch(src);
				const blob = await resp.blob();
				const buffer = await blob.arrayBuffer();
				const bytes = new Uint8Array(buffer);
				let binary = '';
				for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
				return btoa(binary);
			}
			return null;
		} catch (e) {
			return null;
		}
	};

	const autoPlaceSignatureOnTemplate = async (sigUrl) => {
		try {
			const bytes = dataUrlToUint8(pdf);
			if (!bytes) return false;

			const loadingTask = pdfjs.getDocument({ data: bytes });
			const doc = await loadingTask.promise;

		let targetPage = null;
		let targetPageNumber = -1;
		let items = [];

		for (let pageIndex = doc.numPages; pageIndex >= 1; pageIndex--) {
			const page = await doc.getPage(pageIndex);
			const textContent = await page.getTextContent();
			const pageItems = (textContent && textContent.items) ? textContent.items : [];
			
			const signMatches = [];
			for (let i = 0; i < pageItems.length; i++) {
				const item = pageItems[i];
				const text = (item.str || '').toString().trim();
				if (text === 'Sign') {
					const transform = Array.isArray(item.transform) ? item.transform : [];
					const y = typeof transform[5] === 'number' ? transform[5] : 0;
					signMatches.push({ item, y, index: i });
				}
			}
			
			if (signMatches.length > 0) {
				signMatches.sort((a, b) => a.y - b.y);
				const lowestSign = signMatches[0];
				targetPage = page;
				targetPageNumber = pageIndex;
				items = pageItems;
				break;
			}

			const lineBuckets = new Map();
			for (const it of pageItems) {
				const text = (it.str || '').toString();
				if (!text.trim()) continue;
				const transform = Array.isArray(it.transform) ? it.transform : [];
				const y = typeof transform[5] === 'number' ? Math.round(transform[5]) : 0;
				const existing = lineBuckets.get(y);
				if (existing) {
					existing.push(it);
				} else {
					lineBuckets.set(y, [it]);
				}
			}

			const SPACE_GAP_THRESHOLD = 2;
			const linesWithSign = [];
			
			for (const [y, lineItems] of lineBuckets.entries()) {
				const sorted = lineItems
					.filter((it) => (it.str || '').trim().length > 0)
					.sort((a, b) => {
						const ax = Array.isArray(a.transform) && typeof a.transform[4] === 'number' ? a.transform[4] : 0;
						const bx = Array.isArray(b.transform) && typeof b.transform[4] === 'number' ? b.transform[4] : 0;
						return ax - bx;
					});

				let lineText = '';
				for (let i = 0; i < sorted.length; i++) {
					const it = sorted[i];
					const ch = (it.str || '').toString();
					if (i > 0) {
						const prevX = Array.isArray(sorted[i - 1].transform) && typeof sorted[i - 1].transform[4] === 'number' ? sorted[i - 1].transform[4] : 0;
						const prevW = typeof sorted[i - 1].width === 'number' ? sorted[i - 1].width : 0;
						const currX = Array.isArray(it.transform) && typeof it.transform[4] === 'number' ? it.transform[4] : 0;
						const gap = currX - (prevX + prevW);
						if (gap > SPACE_GAP_THRESHOLD) {
							lineText += ' ';
						}
					}
					lineText += ch;
				}

				const lineTextTrimmed = lineText.trim();
				
				const words = lineTextTrimmed.split(/[\s\.,;:\(\)\[\]\{\}"'!?]+/).filter(w => w.length > 0);
				const hasExactSignWord = words.some(word => word === 'Sign');
				
				if (hasExactSignWord) {
					linesWithSign.push({ y, lineText: lineTextTrimmed });
				}
			}

			if (linesWithSign.length > 0) {
				linesWithSign.sort((a, b) => a.y - b.y);
				const lowestLine = linesWithSign[0];
				targetPage = page;
				targetPageNumber = pageIndex;
				items = pageItems;
				break;
			}
		}

			if (!targetPage) {
				return false; // No page with 'sign' text found
			}

			setPageNum(targetPageNumber - 1); // pageNum is 0-indexed

			const lineBuckets = new Map();
			for (const it of items) {
				const text = (it.str || '').toString();
				if (!text) continue;
				const tr = Array.isArray(it.transform) ? it.transform : [];
				const y = typeof tr[5] === 'number' ? Math.round(tr[5]) : 0;
				const arr = lineBuckets.get(y);
				if (arr) arr.push(it); else lineBuckets.set(y, [it]);
			}

			const lines = Array.from(lineBuckets.entries())
				.map(([y, arr]) => ({
					y,
					items: arr
						.filter((it) => (it.str || '').trim().length > 0)
						.sort((a, b) => {
							const ax = Array.isArray(a.transform) && typeof a.transform[4] === 'number' ? a.transform[4] : 0;
							const bx = Array.isArray(b.transform) && typeof b.transform[4] === 'number' ? b.transform[4] : 0;
							return ax - bx;
						}),
				}))
				.sort((a, b) => a.y - b.y); // bottom to top

			const signAnchors = [];
			const isAlpha = (ch) => typeof ch === 'string' && /^[A-Za-z]$/.test(ch);
			const SPACE_GAP_THRESHOLD = 2;
			for (let i = 0; i < lines.length; i++) {
				const line = lines[i];
				let foundRanges = [];
				for (const it of line.items) {
					const s = (it.str || '').toString().trim();
					if (s === 'Sign') {
						const x = Array.isArray(it.transform) && typeof it.transform[4] === 'number' ? it.transform[4] : 0;
						const w = typeof it.width === 'number' ? it.width : 0;
						const y = Array.isArray(it.transform) && typeof it.transform[5] === 'number' ? it.transform[5] : 0;
						const h = Array.isArray(it.transform) && typeof it.transform[3] === 'number' ? Math.abs(it.transform[3]) : 12;
						foundRanges.push({ leftX: x, rightX: x + w, baselineY: y, fontHeight: h });
					}
				}
				if (!foundRanges.length) {
					const chars = line.items.map((it) => ({
						ch: (it.str || '').toString(),
						x: Array.isArray(it.transform) && typeof it.transform[4] === 'number' ? it.transform[4] : 0,
						w: typeof it.width === 'number' ? it.width : 0,
						y: Array.isArray(it.transform) && typeof it.transform[5] === 'number' ? it.transform[5] : 0,
						h: Array.isArray(it.transform) && typeof it.transform[3] === 'number' ? Math.abs(it.transform[3]) : 12,
					}));
					for (let j = 0; j <= chars.length - 4; j++) {
						const seq = chars.slice(j, j + 4).map(c => (c.ch || '').trim());
						if (seq[0] === 'S' && seq[1] === 'i' && seq[2] === 'g' && seq[3] === 'n') {
							const prev = j > 0 ? chars[j - 1] : null;
							const next = j + 4 < chars.length ? chars[j + 4] : null;
							let leftBoundaryOK = true;
							let rightBoundaryOK = true;
							if (prev) {
								const gap = chars[j].x - (prev.x + prev.w);
								leftBoundaryOK = !isAlpha((prev.ch || '').trim()) || gap > SPACE_GAP_THRESHOLD;
							}
							if (next) {
								const gap = next.x - (chars[j + 3].x + chars[j + 3].w);
								rightBoundaryOK = !isAlpha((next.ch || '').trim()) || gap > SPACE_GAP_THRESHOLD;
							}
							if (leftBoundaryOK && rightBoundaryOK) {
								const start = chars[j];
								const end = chars[j + 3];
								foundRanges.push({ leftX: start.x, rightX: end.x + end.w, baselineY: start.y, fontHeight: start.h });
							}
						}
					}
				}
				for (const range of foundRanges) {
					signAnchors.push({ leftX: range.leftX, rightX: range.rightX, baselineY: range.baselineY, fontHeight: range.fontHeight });
				}
			}

			if (!signAnchors.length) {
				return false;
			}

			// Pick left or right position
			signAnchors.sort((a, b) => a.leftX - b.leftX);
			const targetIndex = isSingleSignature ? 0 : (sign_status === '1' ? 1 : 0);
			const chosen = signAnchors[Math.min(targetIndex, signAnchors.length - 1)];
			const y = chosen.baselineY; // baseline of 'Sign' or underscore line above

			// Load with pdf-lib to draw
			const pdfDoc = await PDFDocument.load(pdf);
			const pages = pdfDoc.getPages();
			const signPage = pages[targetPageNumber - 1]; // targetPageNumber is 1-indexed
			const pageWidth = signPage.getWidth();
			const pageHeight = signPage.getHeight();

			const pngImage = await pdfDoc.embedPng(sigUrl);
			// Determine a reasonable signature size relative to the page
			const natural = await new Promise((resolve) => {
				const img = new Image();
				img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
				img.src = sigUrl;
			});
			const widthTarget = pageWidth * 0.28; // ~28% of page width
			const heightTarget = widthTarget * (natural.h / natural.w);

			// Update signature position: place to the right of 'Sign' and slightly above
			const horizontalGap = Math.max(8, (chosen.fontHeight || 12) * 0.4);
			let drawX = chosen.rightX + horizontalGap;
			const verticalGap = Math.max(6, chosen.fontHeight || 12);
			const drawY = y + verticalGap;
			// Prevent overflow beyond page right edge
			const rightLimit = pageWidth - 12; // small right margin
			if (drawX + widthTarget > rightLimit) {
				drawX = Math.max(12, rightLimit - widthTarget);
			}

			signPage.drawImage(pngImage, {
				x: drawX,
				y: drawY,
				width: widthTarget,
				height: heightTarget,
			});

			if (autoDate) {
				const signedText = `Signed ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
				const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
				const fontSize = 12;
				const textWidth = font.widthOfTextAtSize(signedText, fontSize);
				const textX = drawX + (widthTarget / 2) - (textWidth / 2);
				const textY = drawY - 12; // move timestamp slightly lower as well
				signPage.drawText(signedText, {
					x: textX,
					y: textY,
					size: fontSize,
					font,
					color: rgb(0.074, 0.545, 0.262),
				});
			}

			const pdfBytes = await pdfDoc.save();
			const blob = new Blob([new Uint8Array(pdfBytes)]);
			const newURL = await blobToURL(blob);
			setPdf(newURL);
			// Cache latest base64 for finalize upload
			try { latestPdfBase64Ref.current = await getPdfBase64(newURL); } catch (e) { }
			// Scroll the viewport to the placed signature on the last page
			scrollToSignaturePosition(drawX, drawY, pageHeight);
			setConfirm1(true);
			setStep(2);
			setSignatureURL(null);
			return true;
		} catch (err) {
			console.error('Auto placement failed:', err);
			return false;
		}
	};

	const canDetectSignLabels = async () => {
		try {
			const bytes = dataUrlToUint8(pdf);
			if (!bytes) return false;
			const loadingTask = pdfjs.getDocument({ data: bytes });
			const doc = await loadingTask.promise;

			// Search through all pages starting from the last page
			for (let pageIndex = doc.numPages; pageIndex >= 1; pageIndex--) {
				const page = await doc.getPage(pageIndex);
				const textContent = await page.getTextContent();
				const items = (textContent && textContent.items) ? textContent.items : [];

				const exactItems = items
					.map((it) => (it.str || '').toString().trim())
					.filter(Boolean);
				const hasExactItem = exactItems.some((s) => s === 'Sign');
				if (hasExactItem) return true;

				// Strategy 2: group items into lines using Y position, then build line strings
				// This handles cases where each letter is a separate item
				const lineBuckets = new Map();
				for (const it of items) {
					const text = (it.str || '').toString();
					if (!text) continue;
					const transform = Array.isArray(it.transform) ? it.transform : [];
					const y = typeof transform[5] === 'number' ? Math.round(transform[5]) : 0;
					const existing = lineBuckets.get(y);
					if (existing) {
						existing.push(it);
					} else {
						lineBuckets.set(y, [it]);
					}
				}

				const SPACE_GAP_THRESHOLD = 2; // px gap to infer a space
				const hasWordSignOnAnyLine = Array.from(lineBuckets.values()).some((lineItems) => {
					const sorted = lineItems
						.filter((it) => (it.str || '').trim().length > 0)
						.sort((a, b) => {
							const ax = Array.isArray(a.transform) && typeof a.transform[4] === 'number' ? a.transform[4] : 0;
							const bx = Array.isArray(b.transform) && typeof b.transform[4] === 'number' ? b.transform[4] : 0;
							return ax - bx;
						});

					let line = '';
					let prevX = null;
					let prevWidth = null;
					for (const it of sorted) {
						const text = (it.str || '').toString();
						const transform = Array.isArray(it.transform) ? it.transform : [];
						const x = typeof transform[4] === 'number' ? transform[4] : 0;
						const width = typeof it.width === 'number' ? it.width : 0;
						if (prevX !== null && prevWidth !== null) {
							const gap = x - (prevX + prevWidth);
							if (gap > SPACE_GAP_THRESHOLD) {
								line += ' ';
							}
						}
						line += text;
						prevX = x;
						prevWidth = width;
					}

					const tokens = line.split(/\s+/).filter(Boolean);
					return tokens.some(t => t === 'Sign');
				});
				if (hasWordSignOnAnyLine) return true;

			}

			// No 'sign' text found on any page
			return false;
		} catch (e) {
			return false;
		}
	};

	const updatePdf = async (skipCheck = false) => {
		try {
			message.config({
				top: 100, // The distance from the top of the page
			});

			if (!skipCheck && !confirm1) {
				message.error('Please add a signature to sign');
				return;
			}

			// Basic auth and endpoint validation before uploading
			if (!baseUrl || !token) {
				message.error('Signing endpoint or token not provided.');
				return;
			}

			setStep(4)

			// Robustly derive base64 from current PDF (handles blob and data URLs)
			let base64Payload = latestPdfBase64Ref.current || await getPdfBase64(pdf);
			if (!base64Payload) {
				const resultString1 = pdf.replace('data:application/pdf;base64,', "");
				base64Payload = resultString1.replace('data:application/octet-stream;base64,', "");
			}

			// Prepare audit trail and integrity hashes
			const originalBase64 = (origin || '').replace('data:application/pdf;base64,', '').replace('data:application/octet-stream;base64,', '');
			const originalHash = originalBase64 ? await sha256Base64(originalBase64) : '';
			const signedHash = base64Payload ? await sha256Base64(base64Payload) : '';
			const audit = {
				originalHash,
				signedHash,
				signerEmail: (email || '').toLowerCase(),
				signStatus: sign_status,
				templateId: agreement_template_id,
				pageCount: totalPages,
				userAgent: navigator.userAgent,
				timestamp: new Date().toISOString(),
				singleSignType: isSingleSignature ? '1' : '0',
			};

			const csrfToken = await getCsrfTokenFromApi();
			await axios.post(`${baseUrl}/api/updatesign1`, { 
				file: base64Payload, id: id, 
				singleSignType: isSingleSignature ? '1' : '0', 
				email: (email || '').toLowerCase(), 
				sign_status: sign_status, 
				audit 
			}, {
				headers: {
					Authorization: `Bearer ${token}`,
					'X-CSRF-Token': csrfToken
				},
				withCredentials: true
			});
			message.success('Successfully updated!');
			setSigner('')

			const sender1 = userdata1.fname ? `${userdata1.fname} ${userdata1.lname}` : (signer1 && signer1.toLowerCase());
			const sender2 = userdata2.fname ? `${userdata2.fname} ${userdata2.lname}` : (signer2 && signer2.toLowerCase());
			// notification
			if (isSingleSignature) {
				const data = {
					project_id: null,
					title: "Sign completed",
					category: 2,
					type: "role_changes",
					message: `Congratulations! Your project is complete!`,
					project_link: `/active-projects/${id}`,
					assigned_useremail: (email || '').toLowerCase(),
					notification: true,
					showingEmail: true,
					buttontext: 'View completed project',
					domain: domain,
					template: 1,
					message_type: 1,
				};
				if (baseUrl2) await request(baseUrl2, {
					method: "POST",
					headers: {
						authorization: `Bearer ${token}`,
						accept: "application/json",
					},
					body: JSON.stringify(data),
				});
			}
			else if (sign_status === '1') {
				// await axios.post(`${baseUrl}/api/updateprojectbystatus`, { id: id, email: email, status: 4 });
				const data = {
					project_id: null,
					title: "Project signed",
					category: 2,
					type: "role_changes",
					message: `${sender1} has signed the agreement and is requesting your signature.`,
					project_link: `/active-projects/${id}?type=tosign`,
					assigned_useremail: sign_status === '1' ? (signer2 && signer2.toLowerCase()) : (signer1 && signer1.toLowerCase()),
					notification: true,
					showingEmail: true,
					domain: domain,
					buttontext: 'Sign now',
					template: 1,
					message_type: 1,
				}

				if (baseUrl2) await request(baseUrl2, {
					method: "POST",
					headers: {
						authorization: `Bearer ${token}`,
						accept: "application/json",
					},
					body: JSON.stringify(data),
				});
			}

			if (!isSingleSignature && sign_status === '2') {
				const data = {
					project_id: null,
					title: "Sign completed",
					category: 2,
					type: "role_changes",
					message: `Congratulations! Your project is complete!`,
					project_link: `/active-projects/${id}`,
					assigned_useremail: signer1 && signer1.toLowerCase(),
					notification: true,
					showingEmail: true,
					buttontext: 'View completed project',
					domain: domain,
					template: 1,
					message_type: 1,
				}

				await request(baseUrl2, {
					method: "POST",
					headers: {
						authorization: `Bearer ${token}`,
						accept: "application/json",
					},
					body: JSON.stringify(data),
				});

				setTimeout(async () => {
					const data2 = {
						project_id: null,
						title: "Sign completed",
						category: 2,
						type: "role_changes",
						message: `Congratulations! Your project is complete!`,
						project_link: `/active-projects/${id}`,
						assigned_useremail: signer2 && signer2.toLowerCase(),
						notification: true,
						showingEmail: true,
						domain: domain,
						buttontext: 'View completed project',
						template: 1,
						message_type: 1,
					}

					if (baseUrl2) await request(baseUrl2, {
						method: "POST",
						headers: {
							authorization: `Bearer ${token}`,
							accept: "application/json",
						},
						body: JSON.stringify(data2),
					});
				}, 10000);
			}

			console.log('PDF file uploaded successfully');
		} catch (error) {
			console.error('Error uploading PDF:', error);
		}
	};

	useEffect(() => {
		// if (queryParams.get('signer')) {
		// 	message.config({
		// 		duration: 10,
		// 		maxCount: 3,
		// 		style: {
		// 			zIndex: 9999,
		// 		},
		// 	});
		// 	const content = `
		// 	By signing this agreement, I acknowledge that the templates provided by SMVRT LEGAL are not legal advice. <br />
		// 	I understand that I am responsible for reviewing and customizing the agreement as needed and consulting with a legal professional.  <br />
		// 	SMVRT LEGAL is not liable for any errors or omissions. [<a target="_blank" style="color: #69B1FF" href="http://143.198.79.229:3000/terms-and-conditions-and-privacy-policy">More Info</a>]
		// 	`;

		// 	message.success({
		// 		content: <span dangerouslySetInnerHTML={{ __html: content }} />,
		// 	});
		// }
		const fetchPdf = async () => {
			try {
				if (!baseUrl || !fileId) {
					setPdfLoading(false);
					return;
				}
				const sep = (fileId && fileId.includes('?')) ? '&' : '?';
				const urlWithBust = `${baseUrl}/${fileId}${sep}t=${Date.now()}`;
				const response = await fetch(urlWithBust, { cache: 'no-store' });
				const ct = (response.headers.get('content-type') || '').toLowerCase();
				if (ct && !(ct.includes('application/pdf') || ct.includes('application/octet-stream') || ct.includes('binary'))) {
					// proceed but we will validate via pdfjs below
				}
				const blob = await response.blob();
				// Size limit: 25MB to avoid excessive memory/DoS
				if (blob.size > 25 * 1024 * 1024) {
					throw new Error('PDF too large');
				}
				const reader = new FileReader();

				reader.onload = () => {
					try {
						const base64Data = btoa(reader.result);
						const dataUrl = `data:application/pdf;base64,${base64Data}`;
						// Light validation: attempt to parse with pdfjs to ensure it's a PDF
						const bytes = (function () {
							const bin = atob(base64Data);
							const len = bin.length; const out = new Uint8Array(len);
							for (let i = 0; i < len; i++) out[i] = bin.charCodeAt(i);
							return out;
						})();
						// Heuristic safety check
						isLikelySafePdf(bytes).then((ok) => {
							if (!ok) { setPdfLoading(false); return; }
							pdfjs.getDocument({ data: bytes }).promise.then((doc) => {
								if (doc.numPages > 0 && doc.numPages <= 500) {
									setPdf(dataUrl);
									setOrigin(dataUrl);
									setPdfLoading(false);
								} else {
									throw new Error('PDF page count out of bounds');
								}
							}).catch(() => {
								setPdfLoading(false);
							});
						});
					} catch {
						setPdfLoading(false);
					}
				};

				reader.onerror = () => {
					console.error('Error reading PDF file');
					setPdfLoading(false);
				};

				reader.readAsBinaryString(blob);
			} catch (error) {
				console.error('Error fetching PDF:', error);
				setPdfLoading(false);
			}
		};

		fetchPdf();

		const receiveMessage = (event) => {
			try {
				const allowed = getAllowedOrigins();
				if (!allowed.includes(event.origin)) return;
				const data = event.data;
				if (!data || typeof data !== 'object') return;
				if (data.type !== 'smvrt:sign-control') return;
				// Whitelisted actions only
				switch (data.action) {
					case 'ping':
						if (event.source && typeof event.source.postMessage === 'function') {
							event.source.postMessage({ type: 'smvrt:sign-ack', ts: Date.now() }, event.origin);
						}
						break;
					default:
						break;
				}
			} catch (_) { /* ignore */ }
		};

		window.addEventListener('message', receiveMessage);

		return () => {
			window.removeEventListener('message', receiveMessage);
		};

	}, []);


	const [scrollY, setScrollY] = useState(0);

	const handleScroll = () => {
		setScrollY(window.scrollY);
	};

	useEffect(() => {
		setScrollY(window.scrollY); // Initialize scrollY on mount
		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	return (
		<div>
			<Header />

			{signer && (
				<div>
					<div
						style={{
							position: 'fixed',
							top: '0px',
							right: '0px',
							textAlign: 'right',
							zIndex: 9999,
							width: '100%',
							height: '42px',
							background: '#fff',
							borderBottom: '1px solid #e2e2e2'
						}}
					>
					</div>
					<div
						style={{
							position: 'fixed',
							top: '0px',
							right: '10px',
							textAlign: 'right',
							marginBottom: '10px',
							marginRight: '120px',
							zIndex: 99999
						}}
					>
						<Popconfirm
							icon={
								<svg style={{ marginRight: '10px', marginTop: '2px' }} width="27" height="28" viewBox="0 0 27 28" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M13.4194 25.6666C14.8883 25.6685 16.343 25.3676 17.7 24.7811C19.057 24.1947 20.2896 23.3343 21.327 22.2494C22.3669 21.1672 23.1916 19.8814 23.7537 18.4656C24.3159 17.0499 24.6043 15.5323 24.6025 13.9999C24.6043 12.4676 24.3159 10.9499 23.7537 9.53421C23.1916 8.1185 22.3669 6.83261 21.327 5.75043C20.2896 4.66552 19.057 3.80514 17.7 3.21871C16.343 2.63228 14.8883 2.33137 13.4194 2.33326C11.9506 2.33137 10.4958 2.63228 9.1388 3.21871C7.78177 3.80514 6.54919 4.66552 5.51186 5.75043C4.47192 6.83261 3.6472 8.1185 3.08508 9.53421C2.52297 10.9499 2.23453 12.4676 2.23634 13.9999C2.23453 15.5323 2.52297 17.0499 3.08508 18.4656C3.6472 19.8814 4.47192 21.1672 5.51186 22.2494C6.54919 23.3343 7.78177 24.1947 9.1388 24.7811C10.4958 25.3676 11.9506 25.6685 13.4194 25.6666Z" stroke="#3265E2" stroke-width="2" stroke-linejoin="round" />
									<path fill-rule="evenodd" clip-rule="evenodd" d="M13.4194 21.5832C13.7901 21.5832 14.1457 21.4295 14.4078 21.156C14.67 20.8825 14.8173 20.5116 14.8173 20.1248C14.8173 19.7381 14.67 19.3671 14.4078 19.0936C14.1457 18.8201 13.7901 18.6665 13.4194 18.6665C13.0486 18.6665 12.6931 18.8201 12.4309 19.0936C12.1688 19.3671 12.0215 19.7381 12.0215 20.1248C12.0215 20.5116 12.1688 20.8825 12.4309 21.156C12.6931 21.4295 13.0486 21.5832 13.4194 21.5832Z" fill="#3265E2" />
									<path d="M13.4189 7V16.3333" stroke="#3265E2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
								</svg>
							}
							placement="leftTop"
							title="Are you sure? This will erase all current signature details."
							onConfirm={() => {
								setTextInputVisible(false);
								setSignatureDialogVisible(false);
								setSignatureURL(null);
								setPdf(origin);
								// setTotalPages(0);
								// setPageNum(0);
								setPageDetails(null);
								setStep(1);
							}}
							onCancel={() => console.log('Cancelled')}
							okText={<span style={{ padding: '12px, 20px, 12px, 20px' }}>Reset Details</span>}
							cancelText="No"
						>
							<a style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								color: "rgb(33, 41, 54)",
								borderRadius: "6px",
								padding: "5px 13px",
								fontSize: "15px",
								border: "1px solid rgb(199, 199, 204)",
								cursor: "pointer",
								userSelect: "none",
								boxSizing: "border-box",
								marginRight: "8px",
								border: '1px solid #212936'
							}}>
								Reset
							</a>
						</Popconfirm>
					</div>
				</div>
			)}

			{pdf && signer && <div className="top-space" style={{ width: '100%', background: '#fff', zIndex: 99 }}></div>}

			<div style={{ display: 'flex', gap: '20px' }}>
				{pdf && signer && (
					<div
						style={{
							position: 'sticky',
							top: '60px',
							backgroundColor: '#fff',
							display: 'flex',
							gap: '20px',
							padding: '30px 20px',
							zIndex: 999,
							boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
							minWidth: '200px',
							maxHeight: '133px',
							borderRadius: '15px',
							margin: '60px 0px 0px 10px'
						}}
					>
						<Steps
							current={step}
							direction="vertical"
							items={[
								{
									title: 'Step1',
									description:
										<div>
											<div style={{ color: "#6F6F6F", fontSize: '18px' }}>Add Signature</div>
											<div>
												{/* {step === 1 && <div style={{ color: '#2762E9', fontSize: '15px' }}>In process</div>}
												{step > 1 && <div style={{ color: '#1CA289', fontSize: '15px' }}>Completed</div>} */}
												<div style={{ color: '#2762E9', fontSize: '15px' }}>In process</div>
											</div>
											<div>
												<div style={styles.controls} className="addSignatureBtn">
													{/* {!signatureURL && ( */}
													<BigButton
														inverted={true}
														// disabled={step === 1 ? false : true}
														marginRight={8}
														title={"Add Signature"}
														onClick={() => setFontSignatureDialogVisible(true)}
													/>
													{/* )} */}
												</div>
											</div>
										</div>,
									icon:
										<div>
											{step === 1 &&
												<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
													<rect x="0.5" y="0.5" width="19" height="19" rx="9.5" stroke="#3265E2" />
													<path d="M9.05321 12.3867C8.91988 12.3867 8.79321 12.3334 8.69988 12.2401L6.81321 10.3534C6.61988 10.1601 6.61988 9.84007 6.81321 9.64673C7.00655 9.4534 7.32655 9.4534 7.51988 9.64673L9.05321 11.1801L12.4799 7.7534C12.6732 7.56007 12.9932 7.56007 13.1865 7.7534C13.3799 7.94673 13.3799 8.26673 13.1865 8.46006L9.40655 12.2401C9.31321 12.3334 9.18655 12.3867 9.05321 12.3867Z" fill="#3265E2" />
													<circle cx="10" cy="10" r="4" fill="#3265E2" />
												</svg>
											}
											{step > 1 &&
												<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
													<rect width="20" height="20" rx="10" fill="#3265E2" />
													<path d="M9.05321 12.3867C8.91988 12.3867 8.79321 12.3334 8.69988 12.2401L6.81321 10.3534C6.61988 10.1601 6.61988 9.84007 6.81321 9.64673C7.00655 9.4534 7.32655 9.4534 7.51988 9.64673L9.05321 11.1801L12.4799 7.7534C12.6732 7.56007 12.9932 7.56007 13.1865 7.7534C13.3799 7.94673 13.3799 8.26673 13.1865 8.46006L9.40655 12.2401C9.31321 12.3334 9.18655 12.3867 9.05321 12.3867Z" fill="white" />
												</svg>
											}
										</div>
								},
							]}
						/>
					</div>
				)}

				<div style={styles.container}>
					{fontSignatureDialogVisible ? (
						<FontSignatureDialog
							visible={fontSignatureDialogVisible}
							onClose={() => setFontSignatureDialogVisible(false)}
							onConfirm={async ({ text, fontFamily, fontSizePx }) => {
								const url = await createSignatureImageFromFont(text, fontFamily, fontSizePx);
								setFontSignatureDialogVisible(false);
								// if (agreement_template_id && agreement_template_id !== '0') {
								if (agreement_template_id) {
									const detectable = await canDetectSignLabels();
									if (detectable) {
										// Queue auto-placement and show confirmation modals
										pendingSignActionRef.current = async () => {
											const newUrl = await autoPlaceSignatureOnTemplate(url);
											if (newUrl) {
												setConfirm1(true);
											}
										};
										setAckOpen(true);
										return;
									}
								}
								// Fallback to manual placement
								setSignatureURL(url);
								setConfirm1(true);
								setStep(2);
								if (tooltip !== "true") {
									handleTourStepChange2(2, url);
								}
							}}
						/>
					) : null}

					{pdfLoading ? (
						<div style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							padding: '40px',
							color: '#6F6F6F'
						}}>
							<div style={{ marginBottom: '20px' }}>
								<Spin size="large" />
							</div>
							<div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>
								Loading PDF
							</div>
							<div style={{ fontSize: '14px', textAlign: 'center' }}>
								Please wait while we prepare your pdf for signing...
							</div>
						</div>
					) : pdf ? (
						<div style={{ overflow: 'visible' }}>
							<div ref={documentRef} style={styles.documentBlock}>
								{textInputVisible ? (
									<DraggableText
										initialText={
											textInputVisible === "date"
												? `${new Date().toLocaleDateString()}`
												: null
										}

										onCancel={() => setTextInputVisible(false)}
										onEnd={setPosition}
										onSet={async (text) => {
											// Add null check for pageDetails
											if (!pageDetails) {
												console.warn('Page details not available yet');
												return;
											}

											const { originalHeight, originalWidth } = pageDetails;
											const scale = originalWidth / documentRef.current.clientWidth;
											let addtional = 0;

											if (position.y < 240) {
												addtional = 2
											}
											else if (position.y >= 240 && position.y < 400) {
												addtional = -30;
											}
											else if (position.y >= 400 && position.y < 600) {
												addtional = -10;
											}
											else if (position.y >= 500 && position.y < 600) {
												addtional = 0;
											}
											else if (position.y >= 600 && position.y < 700) {
												addtional = 0;
											}
											else if (position.y >= 700 && position.y < 800) {
												addtional = 10;
											}
											else if (position.y >= 800 && position.y < 900) {
												addtional = 15;
											}
											else if (position.y >= 900 && position.y < 1000) {
												addtional = 20;
											}

											if (scrollY > 0) {
												addtional = addtional + scrollY + 40;
											}

											const y =
												documentRef.current.clientHeight -
												(position.y +
													(12 * scale) -
													position.offsetY -
													documentRef.current.offsetTop + addtional);

											const x =
												position.x -
												203 -
												position.offsetX -
												documentRef.current.offsetLeft;


											// new XY in relation to actual document size
											const newY =
												(y * originalHeight) / documentRef.current.clientHeight;
											const newX =
												(x * originalWidth) / documentRef.current.clientWidth;

											const pdfDoc = await PDFDocument.load(pdf);

											const pages = pdfDoc.getPages();
											const firstPage = pages[pageNum];

											firstPage.drawText(text, {
												x: newX,
												y: newY,
												size: 20 * scale,
											});

											const pdfBytes = await pdfDoc.save();
											const blob = new Blob([new Uint8Array(pdfBytes)]);

											const URL = await blobToURL(blob);
											// const { originalHeight, originalWidth } = pageDetails;
											// const scale = originalWidth / documentRef.current.clientWidth;
											// const y =
											// 	documentRef.current.clientHeight -
											// 	(position.y +
											// 		(12 * scale) -
											// 		position.offsetY - documentRef.current.offsetTop + scrollY * scale);
											// const x =
											// 	position.x -
											// 	166 -
											// 	position.offsetX -
											// 	documentRef.current.offsetLeft;

											if (textInputVisible === "date") {
												setConfirm2(true);
											}

											// // new XY in relation to actual document size
											// const newY =
											// 	(y * originalHeight) / documentRef.current.clientHeight;
											// const newX =
											// 	(x * originalWidth) / documentRef.current.clientWidth;

											// const pdfDoc = await PDFDocument.load(pdf);

											// const pages = pdfDoc.getPages();
											// const firstPage = pages[pageNum];

											// firstPage.drawText(text, {
											// 	x: newX,
											// 	y: newY,
											// 	size: 20 * scale,
											// });

											// const pdfBytes = await pdfDoc.save();
											// const blob = new Blob([new Uint8Array(pdfBytes)]);

											// const URL = await blobToURL(blob);
											setPdf(URL);
											setPosition(null);
											setTextInputVisible(false);
											setStep(3);
										}}
									/>
								) : null}
								{signatureURL ? (
									<DraggableSignature
										url={signatureURL}
										onCancel={() => {
											setSignatureURL(null);
										}}
										onSet={async () => {
											// Add null check for pageDetails
											if (!pageDetails) {
												console.warn('Page details not available yet');
												message.config({ top: 100 });
												message.error("You can't sign on this page");
												return;
											}

											const { originalHeight, originalWidth } = pageDetails;
											// Use the actual rendered PDF page canvas for precise measurements
											const pageCanvas = documentRef.current?.querySelector('.react-pdf__Page canvas')
												|| documentRef.current?.querySelector('canvas');

											if (!pageCanvas) {
												return;
											}

											const pageRect = pageCanvas.getBoundingClientRect();
											const rect = position && position.rect ? position.rect : null;
											if (!rect) {
												return; // safety
											}

											// Position relative to the rendered page (top-left origin in CSS pixels)
											const relX = rect.left - pageRect.left;
											const relYTop = rect.top - pageRect.top;

											const pageClientWidth = pageCanvas.clientWidth;
											const pageClientHeight = pageCanvas.clientHeight;
											const scale = originalWidth / pageClientWidth;

											// Convert from rendered CSS pixels to PDF units
											const newX = (relX * originalWidth) / pageClientWidth;
											const newYTop = ((pageClientHeight - relYTop) * originalHeight) / pageClientHeight;

											const pdfDoc = await PDFDocument.load(pdf);

											const pages = pdfDoc.getPages();
											const firstPage = pages[pageNum];

											const pngImage = await pdfDoc.embedPng(signatureURL);
											// Compute image display size in PDF units to match on-screen size
											const widthScale = scale;
											// Load the image to get natural dimensions (in CSS pixels)
											const natural = await new Promise((resolve) => {
												const img = new Image();
												img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
												img.src = signatureURL;
											});
											const targetWidth = natural.w * widthScale;
											const targetHeight = natural.h * widthScale;

											// Align image so that its top-left at drop point by subtracting height
											const drawY = newYTop - targetHeight;
											firstPage.drawImage(pngImage, {
												x: newX,
												y: drawY,
												width: targetWidth,
												height: targetHeight,
											});

											if (autoDate) {
												const signedText = `Signed ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
												const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
												const fontSize = 12 * scale; // slightly smaller and closer
												const textWidth = font.widthOfTextAtSize(signedText, fontSize);

												// Center beneath signature image
												const centerX = newX + (targetWidth / 2) - (textWidth / 2);
												const textY = drawY - (6 * scale); // reduce gap from 10 to 6 (scaled)

												firstPage.drawText(signedText, {
													x: centerX,
													y: textY,
													size: fontSize,
													font,
													color: rgb(0.074, 0.545, 0.262),
												});
											}

											const pdfBytes = await pdfDoc.save();
											const blob = new Blob([new Uint8Array(pdfBytes)]);

											const URL = await blobToURL(blob);
											setPdf(URL);
											setPosition(null);
											setSignatureURL(null);
											try { latestPdfBase64Ref.current = await getPdfBase64(URL); } catch (e) { }
											// Scroll to the drop location on the rendered page
											scrollToSignaturePosition(newX, drawY, originalHeight);
											if (agreement_template_id && agreement_template_id !== '0') {
												setPreModalPdf(pdf);
												setManualFlowActive(true);
												setAckOpen(true);
											}
										}}
										onEnd={setPosition}
									/>
								) : null}

								<PagingControl
									pageNum={pageNum}
									setPageNum={setPageNum}
									totalPages={totalPages}
								/>

								<div className="document-container">
									<Document
										file={pdf}
										onLoadSuccess={(data) => {
											setTotalPages(data.numPages);
										}}
									>
										<Page
											style={{ marginTop: '47px', userSelect: 'none' }}
											pageNumber={pageNum + 1}
											width={800}
											height={1200}
											marginTop={20}
											onLoadSuccess={(data) => {
												setPageDetails(data);
											}}
										/>
									</Document>
								</div>


								<PagingControl
									pageNum={pageNum}
									setPageNum={setPageNum}
									totalPages={totalPages}
								/>
							</div>
						</div>
					) : (
						<div style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							minHeight: '500px',
							padding: '40px',
							color: '#6F6F6F'
						}}>
							<div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>
								Document Not Available
							</div>
							<div style={{ fontSize: '14px', textAlign: 'center' }}>
								Unable to load the document. Please try refreshing the page.
							</div>
						</div>
					)}
				</div>
			</div>
			<Tour
				open={tourOpen2}
				current={tourStep2}
				onClose={() => {
					setTourOpen2(false);
					setTextInputVisible(false);
					setSignatureDialogVisible(false);
					setFontSignatureDialogVisible(false);
					setSignatureURL(null);
					setPdf(origin);
					// setTotalPages(0);
					setPageNum(0);
					setPageDetails(null);
					setStep(1);
				}}
				steps={tourSteps2}
				onChange={handleTourStepChange2}
			/>

			{/* Step 1 Modal: Acknowledgement */}
			<Modal
				open={ackOpen}
				title={<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><span>Sign Now:</span></div>}
				className="sign-ack-modal"
				centered
				onCancel={() => {
					if (manualFlowActive && preModalPdf) {
						setPdf(preModalPdf);
					}
					setManualFlowActive(false);
					setAckOpen(false);
				}}
				okText="Sign Now"
				cancelText="Cancel"
				onOk={() => {
					setAckOpen(false);
					setFinalizeOpen(true);
				}}
			>
				<div style={{ color: '#6F6F6F', marginBottom: 12 }}>
					I acknowledge that I am signing a legal document and agree to its terms.
				</div>
				<div style={{ color: '#6F6F6F' }}>
					By signing this agreement, I acknowledge that the templates provided by SMVRT LEGAL are not legal advice. I understand that I am responsible for reviewing and customizing the agreement as needed and consulting with a legal professional. SMVRT LEGAL is not liable for any errors or omissions. <a href="https://app.smvrtlegal.com/terms-and-conditions-and-privacy-policy/" target="_blank">More Info</a>
				</div>
			</Modal>

			{/* Step 2 Modal: Finalize Confirmation */}
			<Modal
				open={finalizeOpen}
				title={
					<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
						{/* Warning icon */}
						<span aria-hidden="true" style={{ display: 'inline-flex' }}>
							<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
								<circle cx="12" cy="12" r="10" fill="#FFF7E6" stroke="#F5A524" strokeWidth="2" />
								<path d="M12 7v7" stroke="#F5A524" strokeWidth="2" strokeLinecap="round" />
								<circle cx="12" cy="17" r="1.25" fill="#F5A524" />
							</svg>
						</span>
						<span style={{ fontWeight: 600, color: '#111827' }}>You are about to sign this document!</span>
					</div>
				}
				className="sign-finalize-modal"
				centered
				onCancel={() => {
					if (manualFlowActive && preModalPdf) {
						setPdf(preModalPdf);
					}
					setManualFlowActive(false);
					setFinalizeOpen(false);
				}}
				okText="Confirm"
				cancelText="Cancel"
				onOk={async () => {
					setFinalizeOpen(false);
					// If an auto-placement action was queued, run it first (ensures PDF reflects placement)
					if (pendingSignActionRef.current) {
						await pendingSignActionRef.current();
						pendingSignActionRef.current = null;
					}
					setManualFlowActive(false);
					// Finalize & Lock now
					await updatePdf(true);
				}}
			>
				{/* Badge */}
				<div>
					<span
						style={{
							display: 'inline-block',
							background: '#FFF7E6',
							color: '#A16207',
							border: '1px solid #FDE68A',
							borderRadius: 6,
							fontSize: 12,
							padding: '3px 6px',
							marginBottom: 12,
						}}
					>
						FINALIZE SIGNATURE
					</span>
				</div>
				<div style={{ color: '#6F6F6F' }}>
					Easy Partner! You're about to finalize your signature on this document. Once confirmed, your signature will be permanently applied and cannot be undone.
				</div>
				<div style={{ marginTop: 10, color: '#6F6F6F' }}>
					To confirm, click Finalize Signature below.
				</div>
			</Modal>
		</div>
	);
}

export default App;
